import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isEmpty, get, intersection } from 'lodash';
import {
  fetchRelatedItems,
  fetchExtraRelatedItems,
  fetchRecommendations,
  fetchExtraRecommendations,
} from 'actions/details';
import { navigateToPlayerPage } from 'actions/player';
import { navigateToAuthPage, resetUserData } from 'actions/app';
import { selectExtraDetailsContentLists } from 'reducers/details/selectors';
import { selectIsParentalControlEnabled, selectParentalControlAge } from 'reducers/parental-lock/selectors';
import { getIsLifecellFlag } from 'reducers/profile/selectors';
import getLocalized from 'utils/get-localized';
import isPgPinNeeded from 'utils/is-pg-pin-needed';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import { CONTENT_TYPES } from 'constants/content-types';
import { FocusableParent } from 'components/navigation';
import useModals from 'hooks/useModals';
import List from 'components/List';
import Seasons from './Seasons';

const SECTION_NAME = 'EXTRA_CONTENT_SECTION';
const LIST_HEIGTH = 665;
const DEFAULT_SOURCE_CAROUSEL_ID = '10016';

const LIST_CONFIG = {
  enterTo: 'last-focused',
  leaveFor: {
    right: '',
    left: '@BACK_BUTTON',
  },
};

const showPinEvent = {
  eventGroup: 'notification',
  eventName: 'PIN request',
  eventValue: 'show notification',
};

const showNotificationEvent = {
  eventGroup: 'notification',
  eventName: 'unavailable content',
  eventValue: 'show notification',
};

const ExtraContentSection = (props) => {
  const { data, isLoading, baseFocusOffset, onItemFocus, lastFocus, reloadData } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const { showEnterPinModal, showLifecellPaymentModal } = useModals();
  const isLifecellUser = useSelector(getIsLifecellFlag);
  const { id: contentId, seasons, relatedContentUrls } = data;
  const hasRelated = !isEmpty(relatedContentUrls);
  const hasSeasons = !isEmpty(seasons);

  const isParentalEnabled = useSelector(selectIsParentalControlEnabled);
  const age = useSelector(selectParentalControlAge);

  const carouselId = useMemo(
    () => new URLSearchParams(history.location.search).get('sourceCarouselId') || DEFAULT_SOURCE_CAROUSEL_ID,
    [history.location.search],
  );

  const lists = useSelector((state) => selectExtraDetailsContentLists(state, { contentId }));

  const onListFocus = useCallback(
    ({ listIndex, itemIndex }) => {
      const { id, content } = lists[listIndex];
      const size = content.length;
      const isRequiresLazyLoad = itemIndex + 5 > size - 1;
      const actualIndex = hasSeasons ? listIndex + 1 : listIndex;
      const offset = baseFocusOffset + actualIndex * LIST_HEIGTH;

      onItemFocus(offset);

      if (isRequiresLazyLoad) {
        if (id === 'recommendations') {
          dispatch(fetchExtraRecommendations({ contentId, carouselId }));
        } else {
          dispatch(fetchExtraRelatedItems({ contentId, listId: id }));
        }
      }
    },
    [lists, hasSeasons, baseFocusOffset, onItemFocus, dispatch, contentId, carouselId],
  );

  const onListItemEnter = useCallback(
    (item, { itemId: selectedItemId }) => {
      const isEpisode = get(item, ['metadata', 'contentType']) === CONTENT_TYPES.EPISODE;
      const itemId = isEpisode ? item.metadata.tvSeriesId : item.targetId;
      const itemType = isEpisode ? CONTENT_TYPES.TV_SERIES : item.type;

      const search = item.sourceCarouselId ? `sourceCarouselId=${item.sourceCarouselId}` : '';

      if (lastFocus) lastFocus.save(`#${selectedItemId}`);

      history.push({ pathname: `/details/${itemType}/${itemId}`, search });
    },
    [lastFocus, history],
  );

  const onSeasonsFocus = useCallback(() => onItemFocus(baseFocusOffset), [baseFocusOffset, onItemFocus]);

  const onSeasonsEnter = useCallback(
    (item, { itemId: selectedItemId }) => {
      const focusBackConfig = { elementToFocus: `#${selectedItemId}` };
      const goToPlayer = () => dispatch(navigateToPlayerPage({ item }));
      const { visibilityRights, visibilityDetails } = item;
      const { pgRating = {} } = data;
      const isSeries = !!data.seasons;
      // For series need to use first episode Id as vodAssetId
      const purchaseId = isSeries ? data.seasons[0]?.episodes?.content[0]?.id : data.id;

      const requiredLogin = visibilityRights !== 'PLAY' && visibilityDetails === 'NOT_AUTHENTICATED';
      const requiredSubs = visibilityDetails === 'MISSING_SUBSCRIPTION';
      let requiredPin = isParentalEnabled && isPgPinNeeded(pgRating, age);

      if (requiredLogin) {
        const onSuccess = `${history.location.pathname}${history.location.search}`;
        dispatch(navigateToAuthPage(`?vodAssetId=${purchaseId}&onSuccess=${onSuccess}`));
        return;
      }
      if (requiredSubs) {
        // If the object is sold by the piece, then need to go to the tariffs page
        const isSinglePurchase = !isEmpty(intersection(data.types, ['EST', 'TVOD']));

        const addInfo = `vod_asset_id=${item.assetId}; content_type=${item.metadata.contentType}`;
        innerAnalyticsVolia.postEvent({ ...showNotificationEvent, addInfo });

        if (isSinglePurchase) {
          history.push(`${history.location.pathname}/tariffs`);
        } else if (isLifecellUser) {
          // Hack while we don't have react-query flow
          const onSuccess = async () => {
            await dispatch(resetUserData());
            await reloadData();
          };
          showLifecellPaymentModal({ id: purchaseId, assetType: 'vodAssetId', onSuccess }, focusBackConfig);
        } else {
          const onSuccess = `${history.location.pathname}${history.location.search}`;
          history.push({
            pathname: '/profile/packages/filter',
            search: `?vodAssetId=${purchaseId}&onSuccess=${onSuccess}`,
          });
        }
        return;
      }
      if (requiredPin) {
        innerAnalyticsVolia.postEvent(showPinEvent);
        showEnterPinModal(focusBackConfig, goToPlayer);
        return;
      }

      goToPlayer();
    },
    [
      age,
      data,
      dispatch,
      history,
      isLifecellUser,
      isParentalEnabled,
      reloadData,
      showEnterPinModal,
      showLifecellPaymentModal,
    ],
  );

  useEffect(() => {
    // Related could be loaded and extra request can break focus
    const isNeedToLoadRelated = hasRelated && isEmpty(lists);
    if (isNeedToLoadRelated) dispatch(fetchRelatedItems({ contentId, relatedContentUrls }));
  }, [contentId, hasRelated, relatedContentUrls, lists, dispatch]);

  useEffect(() => {
    // Recommendations could be loaded and extra request can break focus
    const isNeedToLoadRecommendations = carouselId && isEmpty(lists);
    if (isNeedToLoadRecommendations) dispatch(fetchRecommendations({ contentId, carouselId }));
  }, [carouselId, contentId, dispatch, lists]);

  return (
    <FocusableParent sectionName={SECTION_NAME}>
      {hasSeasons && (
        <Seasons
          seasons={seasons}
          contentId={contentId}
          onFocus={onSeasonsFocus}
          onListItemEnter={onSeasonsEnter}
          isLoading={isLoading}
          sectionName="SEASONS"
        />
      )}
      {lists.map((list, index) => {
        const title = getLocalized(list.titles, list.title || list.titles['UA']);
        return (
          <List
            key={list.id}
            onListItemFocus={onListFocus}
            listIndex={index}
            config={LIST_CONFIG}
            title={title}
            sectionName={`details-list-${list.id}`}
            onListItemEnter={onListItemEnter}
            listId={`related-content-${list.id}`}
            items={list.content}
            listType={'LARGE'}
            extraPadding={240}
          />
        );
      })}
    </FocusableParent>
  );
};

ExtraContentSection.propTypes = {
  data: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  baseFocusOffset: PropTypes.number.isRequired,
  onItemFocus: PropTypes.func.isRequired,
  lastFocus: PropTypes.object,
};

export default ExtraContentSection;
